import React, { type FC, useState, useMemo } from 'react'
import { Form, Select, DatePicker, type FormInstance } from 'antd'
import { formatCapitalizedString } from '@choreograph/layout'
import type { Dayjs } from 'dayjs'

import { useGetNamespacesQuery } from '../../../state/apis/generated/namespacesApi'
import { useGetResourceTypesQuery } from '../../../state/apis/generated/resourceTypesApi'
import { useGetRelationsQuery } from '../../../state/apis/generated/relationsApi'
import { TreeSelectInput } from '../../shared/TreeSelectInput'
import { useResourcesTreeData } from '../../../constants/hooks/useResourcesTreeData'
import { ISelectOption, IUserPermission } from '../../../constants/types/common'

interface IUserPermissionFormValues extends Omit<IUserPermission, 'expiresAt'> {
  expiresAt?: Dayjs | null
}

interface IAddUserPermissionsFormProps {
  form: FormInstance<IUserPermissionFormValues>
  mode?: 'create' | 'edit'
}

export const AddUserPermissionsForm: FC<IAddUserPermissionsFormProps> = ({ form, mode = 'create' }) => {
  const { data: namespaces = [] } = useGetNamespacesQuery()
  const namespace = form.getFieldValue('namespace')

  const { data: resourceTypes = [] } = useGetResourceTypesQuery({ namespaces: namespace })
  const { data: relations = [] } = useGetRelationsQuery({
    resourceTypes: [`${namespace}/${form.getFieldValue('resourceType')}`]
  }, {
    skip: !namespace || !form.getFieldValue('resourceType')
  })
  const [fieldDisabledMap, setFieldDisabledMap] = useState({ resourceType: true, relation: true })
  const namespaceOptions: ISelectOption[] = useMemo(() => (
    // todo: check type after BE updating
    (namespaces as string[]).map(n => ({ label: formatCapitalizedString(n), value: n }))
  ), [namespaces])

  const resourceTypeOptions: ISelectOption[] = useMemo(() => (
    // todo: check type after BE updating
    (resourceTypes as string[]).map(t => {
      const type: string = t.split('/')[1]
      const label = formatCapitalizedString(type.split('_').join(' '))
      return { label, value: type }
    })
  ), [resourceTypes])

  const relationOptions: ISelectOption[] = useMemo(() => {
    const relationsData = relations as Array<{ relations: string[] }>
    if (relationsData.length) {
      return relationsData[0].relations.map(r => {
        const label = formatCapitalizedString(r.split('_').join(' '))
        return { label, value: r }
      })
    }
    return []
  }, [relations])

  const {
    treeData: resourceTreeData,
    isLoading: resourcesLoading,
    loadChildren
  } = useResourcesTreeData({
    namespace,
    resourceType: form.getFieldValue('resourceType'),
    skip: !namespace || !form.getFieldValue('resourceType')
  })

  const handleSelect = (arg) => {
    if (arg.namespace) {
      setFieldDisabledMap(prev => ({ ...prev, resourceType: false }))
      form.setFieldValue('resourceType', undefined)
      form.setFieldValue('resourceId', undefined)
      form.setFieldValue('relation', undefined)
    }

    if (arg.resourceType) {
      setFieldDisabledMap(prev => ({ ...prev, relation: false }))
      form.setFieldValue('resourceId', undefined)
      form.setFieldValue('relation', undefined)
    }
  }

  return (
    <Form
      requiredMark={false}
      onValuesChange={handleSelect}
      form={form}>
      <Form.Item
        help='This is the project namespace wherein the permission will belong.'
        layout='vertical'
        name='namespace'
        label={<b className='add-permission__label'>1. Namespace</b>}
        rules={[{ required: true }]}>
        <Select
          aria-label='namespace-select'
          placeholder='Select a Namespace'
          options={namespaceOptions}
        />
      </Form.Item>

      <Form.Item
        help='The type of item you would like to add the permission for'
        layout='vertical'
        name='resourceType'
        label={<b className='add-permission__label'>2. Resource Type</b>}
        rules={[{ required: true }]}>
        <Select
          aria-label='resourceType-select'
          options={resourceTypeOptions}
          placeholder='Select a Resource Type'
        />
      </Form.Item>

      <Form.Item
        help='The unique id of the item you’d like to add the permission for'
        layout='vertical'
        name='resourceId'
        label={<b className='add-permission__label'>3. Resource ID</b>}
        rules={[{ required: true }]}
      >
        <TreeSelectInput
          value={form.getFieldValue('resourceId')}
          options={resourceTreeData}
          onChange={(val) => form.setFieldValue('resourceId', val)}
          onLoadData={loadChildren}
          loading={resourcesLoading}
          disabled={!form.getFieldValue('resourceType')}
          placeholder='Select a Resource'
          inputPlaceholder='Enter the Resource ID'
          switchLabels={{ list: 'Browse', input: 'Manual' }}
        />
      </Form.Item>

      <Form.Item
        help='The privilege you’d like to grant to the user for the item chosen'
        layout='vertical'
        name='relation'
        label={<b className='add-permission__label'>4. Relation</b>}
        rules={[{ required: true }]}>
        <Select
          aria-label='relation-select'
          disabled={mode === 'edit' ? false : fieldDisabledMap.relation}
          options={relationOptions}
          placeholder='Select a Relation'
        />
      </Form.Item>
      <Form.Item
        help='Optional expiration date for the permission (UTC)'
        layout='vertical'
        name='expiresAt'
        label={<b className='add-permission__label'>5. Expires At (Optional)</b>}>
        <DatePicker
          aria-label='expiresAt-picker'
          placeholder='Select expiration date'
          showTime={false}
          format='YYYY-MM-DD'
          style={{ width: '100%' }}
        />
      </Form.Item>    </Form>
  )
}
